/**
 * <GuestWorkspace> · ADR-FO-1 §0A · Reception Workspace Principle
 * ────────────────────────────────────────────────────────────────
 * The receptionist's primary operating screen. Contains the 9 canonical tabs
 * (Overview · Reservation · Stay · Room · Folio · Payments · Timeline ·
 * Documents · Notes). Zero business logic — accepts child components for
 * every tab and exposes loading / empty / error / locked / readonly states.
 *
 * i18n: every visible string via `labels` prop map.
 */
import React from "react";
import {
  User, CalendarClock, BedDouble, DoorClosed, Receipt, CreditCard,
  History, FileText, StickyNote, X, Lock, AlertOctagon, Loader2,
} from "lucide-react";
import { OperationalButton, StatusChip } from "./index";
import {
  Sheet, SheetContent, SheetTitle,
} from "@/components/ui/sheet";

const DEFAULT_TABS = [
  { key: "overview",    icon: User,          label: "Overview" },
  { key: "reservation", icon: CalendarClock, label: "Reservation" },
  { key: "stay",        icon: BedDouble,     label: "Stay" },
  { key: "room",        icon: DoorClosed,    label: "Room" },
  { key: "folio",       icon: Receipt,       label: "Folio" },
  { key: "payments",    icon: CreditCard,    label: "Payments" },
  { key: "timeline",    icon: History,       label: "Timeline" },
  { key: "documents",   icon: FileText,      label: "Documents" },
  { key: "notes",       icon: StickyNote,    label: "Notes" },
];

export function GuestWorkspace({
  open, onOpenChange,
  guest,               // {id, name, vip?, primary_phone?, primary_email?}
  headerStatus,        // <StatusChip> element or falsy
  tabs = DEFAULT_TABS, // override to hide tabs, but never remove core 9 by convention
  activeTab = "overview",
  onTabChange,
  children,            // function(tabKey) => ReactNode  OR  object{tabKey: ReactNode}
  primaryAction,       // {label, icon, onClick, tone}
  secondaryActions = [],
  loading = false,
  error = null,        // {title, body} or null
  locked = false,      // if true — banner + disable actions
  readOnly = false,
  emptyLabel = "No guest selected",
  testid = "guest-workspace",
}) {
  const renderTabBody = () => {
    if (loading) return <StateLoading/>;
    if (error) return <StateError title={error.title} body={error.body}/>;
    if (!guest) return <StateEmpty label={emptyLabel}/>;
    if (typeof children === "function") return children(activeTab);
    if (children && typeof children === "object") return children[activeTab] ?? <StateEmpty label="No content"/>;
    return <StateEmpty label="No content for this tab"/>;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" hideCloseButton
        className="w-full sm:max-w-[880px] p-0 flex flex-col"
        data-testid={testid}>
        <SheetTitle className="sr-only">
          {guest ? `Guest ${guest.name}` : emptyLabel}
        </SheetTitle>

        {/* Header */}
        <div className="p-5 border-b-2 border-slate-200 bg-slate-50" data-testid={`${testid}-header`}>
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[20px] font-bold truncate font-display">
                  {guest?.name || emptyLabel}
                </span>
                {guest?.vip && (
                  <StatusChip status="active" label="VIP" size="sm"
                                testid={`${testid}-vip`}/>
                )}
                {headerStatus}
                {readOnly && (
                  <StatusChip status="pending" label="Read-only" size="sm"
                                testid={`${testid}-readonly`}/>
                )}
                {locked && (
                  <StatusChip status="critical" label="Locked" size="sm"
                                testid={`${testid}-locked`}/>
                )}
              </div>
              {guest && (guest.primary_phone || guest.primary_email) && (
                <div className="text-[13px] text-slate-500 truncate">
                  {[guest.primary_phone, guest.primary_email].filter(Boolean).join(" · ")}
                </div>
              )}
            </div>
            <OperationalButton
              label="Close" icon={X} tone="secondary" size="secondary"
              onClick={() => onOpenChange(false)}
              testid={`${testid}-close`}/>
          </div>

          {locked && (
            <div className="mt-3 flex items-center gap-2 text-[12px] text-red-800 bg-red-50 border border-red-300 rounded-lg px-3 py-2" data-testid={`${testid}-lock-banner`}>
              <Lock className="w-4 h-4 shrink-0"/>
              <span>This guest is locked. Contact a supervisor to unlock.</span>
            </div>
          )}
        </div>

        {/* Tabs bar — horizontally scrollable on narrow screens */}
        <div className="border-b border-slate-200 bg-white overflow-x-auto" data-testid={`${testid}-tabs`}>
          <div className="flex gap-0 min-w-max">
            {tabs.map(t => {
              const Icon = t.icon;
              const active = t.key === activeTab;
              return (
                <button key={t.key}
                  onClick={() => onTabChange?.(t.key)}
                  data-testid={`${testid}-tab-${t.key}`}
                  disabled={loading || !!error}
                  className={[
                    "flex items-center gap-1.5 px-4 py-3 text-[14px] font-medium",
                    "min-h-[48px] whitespace-nowrap border-b-4 transition-colors",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400",
                    active
                      ? "border-sky-600 text-sky-800 bg-sky-50"
                      : "border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-50",
                  ].join(" ")}
                >
                  <Icon className="w-4 h-4"/>
                  <span>{t.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto bg-white" data-testid={`${testid}-body`}>
          {renderTabBody()}
        </div>

        {/* Footer actions */}
        {(primaryAction || secondaryActions.length > 0) && (
          <div className="p-4 border-t-2 border-slate-200 bg-white flex flex-wrap items-center gap-2"
                 data-testid={`${testid}-footer`}>
            <div className="flex-1 flex items-center gap-2 min-w-0 flex-wrap">
              {secondaryActions.map((a, i) => (
                <OperationalButton key={a.testid || i} {...a}
                  size="standard" tone={a.tone || "secondary"}
                  disabled={a.disabled || locked || readOnly}/>
              ))}
            </div>
            {primaryAction && (
              <OperationalButton {...primaryAction} size="primary"
                 disabled={primaryAction.disabled || locked || readOnly}/>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ── Shared placeholder states ──────────────────────────────────────
function StateLoading() {
  return (
    <div className="p-10 flex flex-col items-center justify-center gap-3 text-slate-500"
      data-testid="guest-workspace-state-loading">
      <Loader2 className="w-8 h-8 animate-spin"/>
      <span className="text-[14px]">Loading guest…</span>
    </div>
  );
}
function StateEmpty({ label }) {
  return (
    <div className="p-10 flex flex-col items-center justify-center gap-3 text-slate-500"
      data-testid="guest-workspace-state-empty">
      <User className="w-10 h-10 text-slate-300"/>
      <span className="text-[14px]">{label}</span>
    </div>
  );
}
function StateError({ title, body }) {
  return (
    <div className="p-10 flex flex-col items-center justify-center gap-3 text-red-700"
      data-testid="guest-workspace-state-error">
      <AlertOctagon className="w-10 h-10"/>
      <span className="text-[15px] font-semibold">{title || "Something went wrong"}</span>
      {body && <span className="text-[13px] text-slate-600 text-center max-w-md">{body}</span>}
    </div>
  );
}
